import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { Router } from '@angular/router';

type SliderCard = {
  title: string;
  subtitle: string;
  img: string;
  imgAlt: string;
  desc: string;
};

@Component({
  selector: 'app-elevplus-classes',
  templateUrl: './elevplus-classes.component.html',
  styleUrls: ['./elevplus-classes.component.css'],
  standalone: false
})
export class ElevplusClassesComponent implements OnInit, AfterViewInit {

  // ── Slider (Strategy section) ─────────────────────────────────────
  @ViewChild('sliderViewport', { static: false }) sliderViewport!: ElementRef<HTMLElement>;
  @ViewChildren('sliderCard') sliderCards!: QueryList<ElementRef<HTMLElement>>;

  sliderCurrentIndex = 0;
  showOverlayIndex: number | null = null;
  private sliderStep = 0;
  private sliderPositions: number[] = [];
  private isDragging = false;
  private dragStartX = 0;
  private dragScrollStart = 0;

  sliderData: SliderCard[] = [
    {
      title: 'Tailored',
      subtitle: 'For Success',
      img: 'assets/images/ep-sl-1.png',
      imgAlt: 'Tailored learning',
      desc: '<ul><li>Personalised study plans</li><li>Regular progress tracking</li><li>Expert guidance</li></ul>'
    },
    {
      title: 'Interactive',
      subtitle: 'Lectures',
      img: 'assets/images/ep-sl-2.png',
      imgAlt: 'Interactive lectures',
      desc: '<ul><li>Board-wise experienced teachers</li><li>Activate prior learning</li><li>Quick lecture summary videos</li></ul>'
    },
    {
      title: 'All Round',
      subtitle: 'Learning',
      img: 'assets/images/ep-sl-3.png',
      imgAlt: 'All round learning',
      desc: '<ul><li>Technological aids</li><li>Individualised support</li><li>High depth of concepts</li></ul>'
    },
    {
      title: 'Consistent',
      subtitle: 'Assessment',
      img: 'assets/images/ep-sl-4.png',
      imgAlt: 'Consistent assessment',
      desc: '<ul><li>Class tests</li><li>Home tests</li><li>Unit / Topic tests</li><li>Full mock tests</li></ul>'
    },
    {
      title: 'Error Log',
      subtitle: '& Feedback',
      img: 'assets/images/ep-sl-5.png',
      imgAlt: 'Error log and feedback',
      desc: '<ul><li>Parent-Teacher Meetings (PTM)</li><li>Self assessment & reflection</li><li>Highlight weak & strong areas</li></ul>'
    },
    {
      title: 'Full Mock Tests',
      subtitle: '& Past Papers',
      img: 'assets/images/ep-sl-6.png',
      imgAlt: 'Full mock tests',
      desc: '<ul><li>RAR → Error Log → Feedback → PTM</li><li>Free online practice tests</li><li>Video solutions included</li></ul>'
    },
    {
      title: 'Analysis',
      subtitle: 'Feedback & Improve',
      img: 'assets/images/ep-sl-7.png',
      imgAlt: 'Analysis and improve',
      desc: '<ul><li>Result Analysis Report (RAR)</li><li>Reinforcement of weak areas</li><li>Targeted improvement plan</li></ul>'
    }
  ];

  // ── Lifecycle ─────────────────────────────────────────────────────
  constructor(private router: Router, private elRef: ElementRef) {}

  ngOnInit(): void {
    setTimeout(() => this.checkReveal(), 100);
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.measureSlider();
      this.checkReveal();
    }, 200);

    if (this.sliderViewport) {
      const el = this.sliderViewport.nativeElement;
      let ticking = false;
      el.addEventListener('scroll', () => {
        if (!ticking) {
          requestAnimationFrame(() => { this.updateSliderIndex(); ticking = false; });
          ticking = true;
        }
      }, { passive: true });
    }
  }

  // ── Slider logic ──────────────────────────────────────────────────
  private measureSlider(): void {
    if (!this.sliderViewport || !this.sliderCards) { return; }
    const el = this.sliderViewport.nativeElement;
    this.sliderPositions = this.sliderCards.toArray()
      .map(c => c.nativeElement.offsetLeft - el.offsetLeft);
    this.sliderStep = this.sliderPositions[1] !== undefined
      ? this.sliderPositions[1] - this.sliderPositions[0]
      : el.clientWidth;
    this.updateSliderIndex();
  }

  private updateSliderIndex(): void {
    if (!this.sliderViewport || this.sliderStep <= 1) { return; }
    const el = this.sliderViewport.nativeElement;
    this.sliderCurrentIndex = Math.round(el.scrollLeft / this.sliderStep);
  }

  sliderGoTo(i: number): void {
    if (!this.sliderViewport) { return; }
    const el = this.sliderViewport.nativeElement;
    const x = this.sliderPositions[i] !== undefined ? this.sliderPositions[i] : i * this.sliderStep;
    el.scrollTo({ left: x, behavior: 'smooth' });
  }

  sliderPrev(): void { this.sliderGoTo(Math.max(0, this.sliderCurrentIndex - 1)); }
  sliderNext(): void { this.sliderGoTo(Math.min(this.sliderData.length - 1, this.sliderCurrentIndex + 1)); }

  toggleOverlay(i: number): void {
    this.showOverlayIndex = this.showOverlayIndex === i ? null : i;
  }
  hideOverlay(): void { this.showOverlayIndex = null; }

  onSliderKeydown(e: KeyboardEvent): void {
    if (e.key === 'ArrowLeft')  { e.preventDefault(); this.sliderPrev(); }
    if (e.key === 'ArrowRight') { e.preventDefault(); this.sliderNext(); }
    if (e.key === 'Home')       { e.preventDefault(); this.sliderGoTo(0); }
    if (e.key === 'End')        { e.preventDefault(); this.sliderGoTo(this.sliderData.length - 1); }
  }

  sliderDragStart(e: PointerEvent): void {
    this.isDragging = true;
    const el = this.sliderViewport.nativeElement;
    el.setPointerCapture(e.pointerId);
    this.dragStartX = e.clientX;
    this.dragScrollStart = el.scrollLeft;
  }

  sliderDragMove(e: PointerEvent): void {
    if (!this.isDragging) { return; }
    const el = this.sliderViewport.nativeElement;
    el.scrollLeft = this.dragScrollStart - (e.clientX - this.dragStartX);
  }

  sliderDragEnd(e?: PointerEvent): void {
    this.isDragging = false;
    if (e && this.sliderViewport) {
      this.sliderViewport.nativeElement.releasePointerCapture(e.pointerId);
    }
  }

  // ── Scroll reveal ─────────────────────────────────────────────────
  @HostListener('window:scroll')
  onScroll(): void { this.checkReveal(); }

  @HostListener('window:resize')
  onResize(): void { this.measureSlider(); }

  private checkReveal(): void {
    const els = this.elRef.nativeElement.querySelectorAll('.ep-reveal');
    const wh = window.innerHeight;
    els.forEach((el: Element) => {
      if (el.getBoundingClientRect().top < wh * 0.9) {
        el.classList.add('ep-vis');
      }
    });
  }

  // ── Navigation ────────────────────────────────────────────────────
  scrollTo(id: string): void {
    const el = document.getElementById(id);
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
  }

  navigate(path: string): void { this.router.navigate([path]); }

  bookFreeDemo() {
    window.open('https://go.sehyog.co.uk/FreeDemoForm', '_blank');
  }
}